import React, { useEffect, useState } from "react";
import { Sparkles, RefreshCw, Loader2, Quote as QuoteIcon } from "lucide-react";
import api from "../../lib/api";

/**
 * AI-generated daily motivation quote — full-width horizontal banner that
 * sits between the dashboard header and the KPI strip.
 *
 * Layout: [icon] [DAILY SPARK label + date] | [quote text with typewriter] | [author] [refresh]
 *
 * Animations:
 *  - Sweeping conic-gradient border (cyan → gold, 6 s loop)
 *  - Typewriter reveal of the quote with blinking cursor
 *  - Pulsing sparkle icon + subtle scan-line shimmer across the banner
 */
export default function DailyQuote() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typed, setTyped] = useState("");

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/dashboard/daily-quote");
      setData(r.data);
      setTyped("");
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  // Typewriter
  useEffect(() => {
    if (!data?.quote) return;
    setTyped("");
    const full = data.quote;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTyped(full.slice(0, i));
      if (i >= full.length) clearInterval(id);
    }, 22);
    return () => clearInterval(id);
  }, [data?.quote]);

  return (
    <div className="daily-quote-banner relative w-full" data-testid="daily-quote">
      <div className="daily-quote-border" aria-hidden="true" />
      <div className="daily-quote-shimmer" aria-hidden="true" />
      <div className="relative z-10 flex items-center gap-4 rounded-md bg-[#02152E]/95 px-4 py-3 backdrop-blur-sm md:gap-5 md:px-5 md:py-4">
        {/* Left badge */}
        <div className="flex shrink-0 items-center gap-3">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-ncc-gold/15 ring-1 ring-ncc-gold/45 md:h-11 md:w-11">
            <Sparkles className="h-5 w-5 animate-pulse text-ncc-gold" />
          </div>
          <div className="hidden flex-col leading-tight sm:flex">
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">Daily Spark</span>
            <span className="font-mono text-[10px] text-slate-400">AI · {data?.date_key || "today"}</span>
          </div>
        </div>

        {/* Divider */}
        <span className="hidden h-10 w-px shrink-0 bg-gradient-to-b from-transparent via-cyan-500/40 to-transparent sm:block" />

        {/* Quote */}
        <div className="min-w-0 flex-1">
          {loading && !data ? (
            <span className="inline-flex items-center gap-2 font-mono text-sm text-slate-300">
              <Loader2 className="h-4 w-4 animate-spin" /> Summoning today&apos;s spark…
            </span>
          ) : (
            <div className="flex items-baseline gap-2">
              <QuoteIcon className="h-4 w-4 shrink-0 -translate-y-0.5 text-ncc-gold opacity-80" />
              <p className="font-heading text-sm font-semibold leading-snug text-white sm:text-base md:text-lg">
                {typed || data?.quote || ""}
                {typed && typed.length < (data?.quote?.length || 0) && (
                  <span className="dq-caret" aria-hidden="true">|</span>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Author + refresh */}
        <div className="hidden shrink-0 items-center gap-3 md:flex">
          {data?.author && (
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Source</div>
              <div className="font-mono text-xs text-cyan-200">— {data.author}</div>
            </div>
          )}
          <button
            onClick={load}
            disabled={loading}
            data-testid="daily-quote-refresh"
            title="Re-fetch today's quote"
            className="inline-flex h-8 w-8 items-center justify-center rounded border border-cyan-500/40 text-cyan-200 transition-all hover:-translate-y-0.5 hover:bg-cyan-500/15 hover:text-white disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
